import { Flex } from 'antd'
import { CSSProperties, FC, useEffect, useMemo } from 'react'
import { CarrierOneClimat } from '../../components/CarrierOneClimat'
import { CarrierOneConsumers } from '../../components/CarrierOneConsumers'
import { CarrierOneHome } from '../../components/CarrierOneHome'
import { CarrierOneLightning } from '../../components/CarrierOneLightning'
import CarrierOneLogging from '../../components/CarrierOneLogging'
import { CarrierOneMonitoring } from '../../components/CarrierOneMonitoring'
import { CarrierOneSettings } from '../../components/CarrierOneSettings'
import { CarrierOneSidebar } from '../../components/CarrierOneSidebar'
import CarrierOneTabs from '../../components/CarrierOneTabs'
import SensorDataChart from '../../components/SensorDataChart'
import { AuthWarning } from '../../components/AuthWarning'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { authStore } from '../../store/auth'
import { loggingStore } from '../../store/logging'
import { canvasStore } from '../../store/canvas'
import { sensorStore } from '../../store/sensor'
import { carrierPermissionStore } from '../../store/carrierPermission'
import { RNCarriers } from '../../helpers/routeNames'
import Cookies from 'universal-cookie'

export const Common: FC = () => {
	const sideBarShown = true
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const cookie = useMemo(() => new Cookies(), [])

	const { user, isLoggedIn } = authStore()
	const { fetchLoggings } = loggingStore()
	const { fetchCanvasData } = canvasStore()
	const { fetchSensors } = sensorStore()
	const { carrierPermission } = carrierPermissionStore()

	// Authenticated if the store says so (updated by App.tsx init or manual refresh)
	const isAuthenticated = isLoggedIn || !!user

	// Try to refresh tokens if we have refresh token but no access token
	useEffect(() => {
		const tryRefresh = async () => {
			const accessToken = cookie.get('access')
			const refreshToken = cookie.get('refresh')

			if (!accessToken && refreshToken) {
				try {
					console.log(
						'🔄 Common page: Attempting token refresh...'
					)
					const { refresh: refreshFn } =
						authStore.getState()
					await refreshFn()
					console.log(
						'✅ Common page: Tokens refreshed successfully'
					)
				} catch (error) {
					console.error(
						'❌ Common page: Token refresh failed:',
						error
					)
					// Don't redirect here, let the auth warning show
				}
			}
		}
		tryRefresh()
	}, [cookie])

	const isCarrierPermission =
		user &&
		id &&
		!!carrierPermission[user.id]?.find(
			(el) => el.type === 'settings' && el.carrierId === +id
		)
	const carriersItemsStyle: CSSProperties = {
		display: 'flex',
		gap: '16px',
		padding: '16px'
	}

	useEffect(() => {
		if (!id || isNaN(Number(id))) return
		fetchSensors(+id)
		fetchLoggings(+id!)
		fetchCanvasData(+id)
	}, [id, fetchLoggings, fetchCanvasData, fetchSensors])

	if (!id || isNaN(Number(id))) {
		navigate(RNCarriers)
		return <center>{t('Redirecting')}...</center>
	}

	// Show authentication warning if not authenticated
	if (!isAuthenticated) {
		return <AuthWarning />
	}

	return (
		<main style={carriersItemsStyle}>
			<Flex style={{ overflowY: 'auto' }} gap={16} vertical>
				{location.search === '' ? (
					<Flex
						gap={16}
						style={{
							overflowY: 'auto',
							width: sideBarShown ? '1001px' : '100%',
							height: '100%',
							position: 'relative'
						}}
						className="carrierTabForScrollbar">
						{((!sideBarShown &&
							location.hash === '#start') ||
							sideBarShown) && (
							<CarrierOneSidebar
								carrierId={+id}
								sideBarShown={sideBarShown}
							/>
						)}
						<Flex
							vertical
							gap={16}
							style={{
								width:
									!sideBarShown &&
									location.hash === '#start'
										? 0
										: '100%',
								position:
									!sideBarShown &&
									location.hash === '#start'
										? 'absolute'
										: 'unset'
							}}>
							<CarrierOneTabs
								hash={location.hash}
								disabled={!isCarrierPermission}
								sideBarShown={sideBarShown}
							/>
							{location.hash === '' ? (
								<>
									<CarrierOneHome carrierId={+id} />
									<CarrierOneLogging id={+id} />
								</>
							) : location.hash === '#lightning' ? (
								<CarrierOneLightning
									carrierId={+id}
									sideBarShown={sideBarShown}
								/>
							) : location.hash === '#consumers' ? (
								<CarrierOneConsumers
									carrierId={+id}
									sideBarShown={sideBarShown}
								/>
							) : location.hash === '#climat' ? (
								<CarrierOneClimat
									carrierId={+id}
									sideBarShown={sideBarShown}
								/>
							) : location.hash.includes(
									'#monitoring'
							  ) ? (
								<CarrierOneMonitoring id={+id} />
							) : location.hash.includes('#settings') &&
							  isCarrierPermission ? (
								<CarrierOneSettings carrierId={+id} />
							) : (
								''
							)}
						</Flex>
					</Flex>
				) : location.search === '?desc' ? (
					<h1 style={{ margin: 0 }}>{t('Coming Soon!')}</h1>
				) : location.search === '?diagram' ? (
					<SensorDataChart />
				) : location.search === '?logging' ? (
					<CarrierOneLogging id={+id} />
				) : (
					<></>
				)}
			</Flex>
		</main>
	)
}
